import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetCirculationFilters } from 'types';
import moment from 'moment';
import CirculationList from 'components/templates/circulation-list';
import Layout from 'components/templates/layout';
import { circulationsActions, circulationsSelectors } from 'common/config/store';
import { useIntl } from 'common/config/i18n';
import CirculationFilter from 'components/templates/circulation-filter';
import { StyledMainTitle, StyledListUtil } from 'common/styling/mixins';

const { StyledListWrapper, StyledListTitleWrapper } = StyledListUtil;

const getServiceParamsFromFilterData = (filterData?: GetCirculationFilters) => {
  const todayDate = moment().format('YYYY-MM-DD');
  return {
    serviceName: filterData && filterData.serviceName ? filterData.serviceName : '',
    departureStation: filterData && filterData.departureStation ? filterData.departureStation : '',
    arrivalStation: filterData && filterData.arrivalStation ? filterData.arrivalStation : '',
    startDate: filterData && filterData.startDate ? filterData.startDate : todayDate,
    endDate: filterData && filterData.endDate ? filterData.endDate : todayDate,
    weekdays: filterData && filterData.weekdays ? filterData.weekdays : '1234567',
    pageNumber: filterData && filterData.pageNumber ? filterData.pageNumber : 1,
    maxResultPage: filterData && filterData.maxResultPage ? filterData.maxResultPage : 10,
    includedCancel: true, // Filter not persisted
    includedRolling: false, // Filter not persisted
    serviceTypeIDs: undefined, // Filter not persisted
  };
};

const Circulation: React.FC = () => {
  const { t, currentLocale } = useIntl();
  const dispatch = useDispatch();

  const persistedCirculationsFiltersSelector = useSelector(circulationsSelectors.persistedCirculationsFiltersSelector);
  const shoudUsePersistedCirculationsFilters = useSelector(
    circulationsSelectors.shoudUsePersistedCirculationsFiltersSelector
  );

  const circulationsData = useSelector(circulationsSelectors.dataSelector);
  const circulationsStatus = useSelector(circulationsSelectors.statusSelector);
  const [filterValues, setFilterValues] = useState<GetCirculationFilters>();

  const launchAction = (
    values: GetCirculationFilters,
    action: (params: GetCirculationFilters, language: string) => void
  ) => {
    const params = {
      ...values,
      startDate: moment(values.startDate).format('YYYY-MM-DD'),
      endDate: moment(values.endDate).format('YYYY-MM-DD'),
      pageNumber: 1,
      maxResultPage: 10,
    };
    setFilterValues(params);
    dispatch(action(params, currentLocale));
  };

  useEffect(() => {
    if (!shoudUsePersistedCirculationsFilters) {
      launchAction(
        {
          startDate: moment().format('YYYY-MM-DD'),
          endDate: moment().format('YYYY-MM-DD'),
          pageNumber: 1,
          maxResultPage: 10,
          weekdays: '1234567',
          includedCancel: true,
        },
        circulationsActions.fetchCirculations
      );
    } else {
      const persistedCirculationsFilters = getServiceParamsFromFilterData(persistedCirculationsFiltersSelector);
      launchAction(persistedCirculationsFilters, circulationsActions.fetchCirculations);
      dispatch(circulationsActions.setShoudUsePersistedCirculationsFilters(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const fetchAction = (values: GetCirculationFilters) => {
    launchAction(values, circulationsActions.fetchCirculations);
  };
  const refreshAction = (values: GetCirculationFilters) => {
    launchAction(values, circulationsActions.refreshCirculations);
  };
  const exportListAction = (values: GetCirculationFilters) => {
    launchAction(values, circulationsActions.exportCirculations);
  };

  const onChangePage = (page: number, pageSize?: number) => {
    dispatch(
      circulationsActions.fetchCirculations({
        ...filterValues,
        pageNumber: page,
        maxResultPage: pageSize,
      })
    );
  };

  const persistedFilterParams = getServiceParamsFromFilterData(
    shoudUsePersistedCirculationsFilters ? persistedCirculationsFiltersSelector : undefined
  );

  return (
    <div>
      <Layout />
      <StyledListWrapper>
        <StyledListTitleWrapper>
          <StyledMainTitle>{t('CirculationPage', 'CIRCULATION_TABLE')}</StyledMainTitle>
        </StyledListTitleWrapper>
        <CirculationFilter
          status={circulationsStatus}
          fetchAction={fetchAction}
          refreshAction={refreshAction}
          exportListAction={exportListAction}
          searchDate={circulationsData?.searchTimestamp}
          {...persistedFilterParams}
        />
        {circulationsStatus === 'success' && <CirculationList data={circulationsData} onChangePage={onChangePage} />}
      </StyledListWrapper>
    </div>
  );
};

export default Circulation;
